import { useState, useEffect, useCallback } from 'react';
import { GroupChannelList } from '@sendbird/uikit-react/GroupChannelList';
import { type ChannelListQueryParamsType } from '@sendbird/uikit-react/GroupChannelList/context';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment } from '@fortawesome/pro-regular-svg-icons/faComment';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons/faArrowsRotate';
import { SendbirdSdkProvider } from 'SendbirdSdkProvider';
import useIsMobileMixin from 'StudentNetwork/react/useIsMobileMixin';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { useMessagingConfig } from '../utils/useMessagingConfig';
import '../locales/messaging/messaging-en.json';
import { getActiveChannelUrl, getShowNewMessage } from '../selectors';
import { ChannelListItem } from './ChannelListItem';
import { setActiveChannelUrl, setReplyMessageId, setShowMessageSearch } from '../actions';
import { ChannelListHeader } from './ChannelListHeader';
import { useMessagingSurface } from './MessagingProvider';

function ChannelListLoadingState({ onRefresh }: { onRefresh?: () => void }) {
    const [timedOut, setTimedOut] = useState(false);

    useEffect(() => {
        const id = setTimeout(() => setTimedOut(true), 10000);
        return () => clearTimeout(id);
    }, []);

    if (timedOut) return <ChannelListEmptyState onRefresh={onRefresh} />;
    return <FrontRoyalSpinner color="force-brand" />;
}

function ChannelListEmptyState({ onRefresh }: { onRefresh?: () => void }) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'messaging.messaging' });
    const [showEmpty, setShowEmpty] = useState(false);

    useEffect(() => {
        const id = setTimeout(() => setShowEmpty(true), 2000);
        return () => clearTimeout(id);
    }, []);

    if (!showEmpty) return <FrontRoyalSpinner className="no-delay" color="force-brand" />;

    return (
        <div className="flex h-full flex-col items-center justify-center gap-3">
            <FontAwesomeIcon icon={faComment} className="text-[40px] text-slate-grey-300" />
            <span className="text-base text-slate-grey">{t('noChatsYet')}</span>
            {onRefresh && (
                <button
                    type="button"
                    onClick={onRefresh}
                    className="flex items-center gap-1.5 rounded-5 border border-slate-grey-mid-light px-3 py-1.5 text-sm text-slate-grey transition-colors hover:bg-slate-grey-lightest"
                >
                    <FontAwesomeIcon icon={faArrowsRotate} />
                    {t('refresh')}
                </button>
            )}
        </div>
    );
}

// Sendbird's .sendbird-channel-list sets `display: flex; width: 320px` which
// overrides Tailwind utilities after the bootstrap removal dropped `important: true`.
// Use `!` on display/width utilities so they win over Sendbird's defaults.
const widgetClasses = {
    groupChannelList: (activeChannel: string, showNewMessage: boolean) =>
        clsx('sm:!flex', 'sm:!flex-shrink-0', 'sm:!w-[320px]', { '!hidden': !!activeChannel || showNewMessage }),
};

const networkClasses = {
    groupChannelList: (activeChannel: string, showNewMessage: boolean) =>
        clsx('!w-full', '@[768px]:!flex', '@[768px]:!flex-shrink-0', '@[768px]:!w-[320px]', {
            '!hidden': !!activeChannel || showNewMessage,
        }),
};

export const ChannelList = () => {
    const dispatch = useDispatch();
    const activeChannel = useSelector(getActiveChannelUrl);
    const showNewMessage = useSelector(getShowNewMessage);
    const { isMobile } = useIsMobileMixin();
    const isNetwork = useMessagingSurface() === 'student-network';
    const classes = isNetwork ? networkClasses : widgetClasses;
    const { userId, accessToken } = useMessagingConfig();
    const [refreshKey, setRefreshKey] = useState(0);
    const handleRefresh = useCallback(() => {
        const sdk = SendbirdSdkProvider.sdk;
        if (sdk && userId && accessToken && sdk.connectionState !== 'OPEN') {
            // Connect the standalone SDK first (primes the shared WebSocket),
            // then re-mount GroupChannelList so it queries with a live connection.
            sdk.connect(userId, accessToken)
                .then(() => setRefreshKey(k => k + 1))
                .catch(() => setRefreshKey(k => k + 1));
        } else {
            setRefreshKey(k => k + 1);
        }
    }, [userId, accessToken]);

    return (
        <GroupChannelList
            key={refreshKey}
            channelListQueryParams={{
                includeEmpty: true,
                order: 'latest_last_message' as ChannelListQueryParamsType['order'],
            }}
            className={classes.groupChannelList(activeChannel, showNewMessage)}
            onChannelCreated={groupChannel => {
                dispatch(setActiveChannelUrl(groupChannel.url));
                dispatch(setReplyMessageId(null));
            }}
            disableAutoSelect={showNewMessage ? true : isMobile || isNetwork ? true : !!activeChannel}
            onChannelSelect={groupChannel => {
                if (groupChannel?.url) {
                    dispatch(setShowMessageSearch(false));
                    dispatch(setActiveChannelUrl(groupChannel.url));
                    dispatch(setReplyMessageId(null));
                }
            }}
            renderHeader={() => <ChannelListHeader />}
            selectedChannelUrl={activeChannel}
            renderChannelPreview={p => <ChannelListItem key={p.channel.url} {...p} />}
            renderPlaceHolderLoading={() => <ChannelListLoadingState onRefresh={handleRefresh} />}
            renderPlaceHolderEmptyList={() => <ChannelListEmptyState onRefresh={handleRefresh} />}
        />
    );
};
